import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { List, Plus, CheckSquare, Square, Trash2, Music2, GripVertical, HardDrive } from 'lucide-react';
import { isFileSystemAccessSupported } from '../../utils/localFileManager';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmDialog from '../ConfirmDialog';
import api from '../../utils/api';
import { toast } from 'sonner';

// Draggable Track List Component
const DraggableTrackList = ({ tracks, onDragEnd, onDeleteTrack, selectedTracks, onToggleSelect }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={tracks.map(t => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {tracks.map((track) => (
            <SortableTrackItem
              key={track.id}
              track={track}
              onDelete={onDeleteTrack}
              isSelected={selectedTracks.includes(track.id)}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

// Sortable Track Item Component
const SortableTrackItem = ({ track, onDelete, isSelected, onToggleSelect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`track-item-${track.id}`}
      className={`flex items-center justify-between rounded-lg p-3 group hover:bg-violet-50 transition-colors ${
        isSelected ? 'bg-violet-100 border border-violet-300' : 'bg-gray-50 border border-gray-200'
      }`}
    >
      <div className="flex items-center gap-3 flex-1">
        <button
          onClick={() => onToggleSelect(track.id)}
          className="text-violet-600 hover:text-violet-700 focus:outline-none"
          data-testid={`select-track-${track.id}`}
        >
          {isSelected ? (
            <CheckSquare className="w-5 h-5" />
          ) : (
            <Square className="w-5 h-5" />
          )}
        </button>
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-violet-600 focus:outline-none"
          data-testid={`drag-handle-${track.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Music2 className="w-4 h-4 text-violet-500" />
        <div className="flex-1">
          <p className="text-gray-900 text-sm font-medium">{track.title}</p>
          <p className="text-gray-500 text-xs">{track.artist}</p>
        </div>
      </div>
      <Button
        data-testid={`delete-track-button-${track.id}`}
        onClick={() => onDelete(track.id)}
        size="icon"
        variant="ghost"
        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 hover:bg-red-50"
        type="button"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Upload Button Component
const UploadButton = ({ playlistId, onUpload, uploading }) => {
  const fileInputRef = React.useRef(null);

  const handleClick = () => {
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <Button
        data-testid={`upload-button-${playlistId}`}
        onClick={handleClick}
        disabled={uploading}
        size="sm"
        className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white disabled:opacity-50 transition-all duration-200"
      >
        <Plus className="w-3 h-3 mr-1" />
        {uploading ? 'Envoi...' : 'Ajouter Musiques'}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        multiple
        onChange={(e) => onUpload(Array.from(e.target.files || []))}
        className="hidden"
        data-testid={`file-input-${playlistId}`}
      />
    </>
  );
};

// Main PlaylistManager Component
const PlaylistManager = ({
  playlist,
  onAddTrack,
  onUpload,
  uploading,
  onOpenLocalBrowser  // New prop for local file browser
}) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadTracks();
  }, [playlist.id]);

  const loadTracks = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/playlists/${playlist.id}`);
      setTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Error loading tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tracks.findIndex((t) => t.id === active.id);
    const newIndex = tracks.findIndex((t) => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder locally
    const newTracks = [...tracks];
    const [movedTrack] = newTracks.splice(oldIndex, 1);
    newTracks.splice(newIndex, 0, movedTrack);
    setTracks(newTracks);

    // Update backend
    try {
      const trackIds = newTracks.map((t) => t.id);
      await api.put(`/tracks/reorder?playlist_id=${playlist.id}`, trackIds);
      toast.success('Ordre mis à jour');
    } catch (error) {
      console.error('Error reordering tracks:', error);
      toast.error('Erreur lors du réordonnancement');
      loadTracks();
    }
  };

  const toggleSelectTrack = (trackId) => {
    setSelectedTracks(prev =>
      prev.includes(trackId)
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTracks.length === tracks.length) {
      setSelectedTracks([]);
    } else {
      setSelectedTracks(tracks.map(t => t.id));
    }
  };

  const onRequestDeleteTrack = async (trackId) => {
    if (window.confirm('Supprimer ce titre ?')) {
      try {
        await api.delete(`/tracks/${trackId}`);
        toast.success('Titre supprimé');
        loadTracks();
      } catch (error) {
        console.error('Error deleting track:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedTracks.map(id => api.delete(`/tracks/${id}`)));
      toast.success(`${selectedTracks.length} titre(s) supprimé(s)`);
      setSelectedTracks([]);
      setShowDeleteConfirm(false);
      loadTracks();
    } catch (error) {
      console.error('Error deleting tracks:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="border border-violet-200 rounded-xl p-4 bg-white shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          <List className="w-4 h-4 text-violet-600" />
          {playlist.name} ({playlist.type === 'main' ? 'Principale' : 'Secours'})
        </h4>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid={`add-track-button-${playlist.id}`}
            onClick={onAddTrack}
            size="sm"
            variant="outline"
            className="border-violet-300 text-violet-600 hover:bg-violet-50"
          >
            <Plus className="w-3 h-3 mr-1" />
            Ajouter
          </Button>
          <UploadButton 
            playlistId={playlist.id}
            onUpload={onUpload}
            uploading={uploading}
          />
          {/* Local File Browser Button - Professional DJ workflow */}
          {isFileSystemAccessSupported() && onOpenLocalBrowser && (
            <Button
              data-testid={`local-browser-button-${playlist.id}`}
              onClick={onOpenLocalBrowser}
              disabled={uploading}
              size="sm"
              variant="outline"
              className="border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              <HardDrive className="w-3 h-3 mr-1" />
              Depuis mon PC
            </Button>
          )}
        </div>
      </div>

      {/* Selection Controls */}
      {tracks.length > 0 && (
        <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 transition-colors"
            data-testid={`select-all-tracks-${playlist.id}`}
          >
            {selectedTracks.length === tracks.length ? (
              <CheckSquare className="w-4 h-4" />
            ) : (
              <Square className="w-4 h-4" />
            )}
            <span>
              {selectedTracks.length === tracks.length 
                ? 'Tout désélectionner' 
                : 'Tout sélectionner'}
            </span>
          </button>
          
          {selectedTracks.length > 0 && (
            <Button
              data-testid={`delete-selected-button-${playlist.id}`}
              onClick={() => setShowDeleteConfirm(true)}
              size="sm"
              className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Supprimer {selectedTracks.length} piste{selectedTracks.length > 1 ? 's' : ''}
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm">Chargement...</p>
      ) : tracks.length === 0 ? (
        <p className="text-gray-500 text-sm">Aucun titre</p>
      ) : (
        <DraggableTrackList
          tracks={tracks}
          onDragEnd={handleDragEnd}
          onDeleteTrack={onRequestDeleteTrack}
          selectedTracks={selectedTracks}
          onToggleSelect={toggleSelectTrack}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDeleteSelected}
        title="Supprimer les pistes sélectionnées ?"
        description={`Vous êtes sur le point de supprimer ${selectedTracks.length} piste${selectedTracks.length > 1 ? 's' : ''}. Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="destructive"
        icon={Trash2}
      />
    </div>
  );
};

export default PlaylistManager;
